import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import './FileViewer.css';
import { FetchFileService } from '../../../utils/services/project/project';
import { getFileDetails } from '../../../utils/utils';
import { REACT_APP_CDN_URL } from '../../../utils/base-urls';

type DocData = {
    id: number;
    documentType: string;
}
interface DocumentViewerProps {
    fileId?: number | null;
    filePath: string;
    fileName?: string;
    fileLocation?: string;
    docData?: DocData | null;
    onClose?: () => void;
}

interface FileState {
    fileUrl: string;
    fileType: string;
    fileName: string;
    fileExtension: string;
    fileBlob?: Blob | null;
    headers?: Record<string, string>;
}

const extensionToMimeType: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    mp4: 'video/mp4',
    mp3: 'audio/mpeg',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    js: 'application/javascript',
    zip: 'application/zip',
};


export const getFileType = (url: string, mimeType?: string): string => {
    const mimeTypeMap: Record<string, string> = {
        'application/pdf': 'pdf',
        'video/mp4': 'video',
        'audio/mpeg': 'audio',
        'audio/mp3': 'audio',
        'image/jpeg': 'image',
        'image/png': 'image',
        'image/gif': 'image',
        'application/vnd.ms-excel': 'excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
        'text/plain': 'text',
        'application/msword': 'word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
        'application/vnd.ms-powerpoint': 'powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
        'application/javascript': 'code',
        'application/zip': 'zip'
    };

    const extensionMap: Record<string, string> = {
        'pdf': 'pdf',
        'mp4': 'video',
        'mp3': 'audio',
        'jpg': 'image',
        'jpeg': 'image',
        'png': 'image',
        'gif': 'image',
        'xls': 'excel',
        'xlsx': 'excel',
        'txt': 'text',
        'doc': 'word',
        'docx': 'word',
        'ppt': 'powerpoint',
        'pptx': 'powerpoint',
        'js': 'code',
        'zip': 'zip'
    };

    if (mimeType && mimeType in mimeTypeMap) {
        return mimeTypeMap[mimeType];
    }

    const extension = url?.split('.').pop()?.toLowerCase();
    return extensionMap[extension ?? ''] ?? 'unsupported';
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    filePath,
}) => {
    const [loading, setLoading] = useState(true);
    const [fileType, setFileType] = useState<string>('');
    const [file, setFile] = useState<FileState>({
        fileUrl: '',
        fileType: '',
        fileName: "",
        fileExtension: "",
        fileBlob: null,
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { name, extension } = getFileDetails(filePath);
    const CDN_BASE_URL = REACT_APP_CDN_URL;


    const fetchFile = async(payload: any, signal: AbortSignal) => {
        setLoading(true);
        try {
            const response = await FetchFileService(payload, { signal });
            const extension = filePath?.split('.').pop()?.toLowerCase();

            const mimeType = extension && extensionToMimeType[extension]
                ? extensionToMimeType[extension]
                : response.headers['content-type'] || 'application/octet-stream';

            const blob = new Blob([response.data], { type: mimeType });
            const fileUrl = URL.createObjectURL(blob);

            setFile({ fileUrl: fileUrl, fileBlob: blob, fileType: '', fileExtension: extension || "", fileName: name });
        } catch (err: any) {
            if (err.name !== "AbortError") {
                setErrorMessage("Failed to load document");
            }
        } finally {
            setLoading(false);
        }
    }

    const handleFile = async (type: string, name: string, extension: string, signal: AbortSignal) => {
        try {
            setLoading(true);
            const s3Url = `${CDN_BASE_URL}${filePath}`;
            if (type === 'unsupported') {
                throw new Error('Unsupported file type');
            } else {
                setFile({
                    fileUrl: s3Url,
                    fileType: extension,
                    fileName: name,
                    fileExtension: extension,
                });
            }
        } catch (error: any) {
            if (signal.aborted) {
                console.error('File fetching aborted');
            } else {
                console.error('Error fetching file:', error.message);
                setErrorMessage('Failed to load the file. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }; 

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        const payload = { path: filePath };
        if (filePath) {
            const type = getFileType(filePath) || 'unsupported';
            setFileType(type);
            if (type === "excel") {
                handleFile(type, name, extension, signal);
            } else {
                fetchFile(payload, signal);

            }
        }
        return () => controller.abort();
    }, []);

    const handleLoadComplete = () => {
        setLoading(false);
    };

    const handleIframeLoad = () => {
        setLoading(false);
    };

    const renderDocViewer = (file: FileState) => {

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                    position: 'relative',
                    border: '0px solid #ddd',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    padding: '2px',
                    width: '100%',
                    height: '70vh',
                }}
            >
                <DocViewer
                    documents={[
                        {
                            uri: file.fileUrl,
                            fileType: extension,
                            fileName: `${name}.${extension}`,
                        },
                    ]}
                    pluginRenderers={DocViewerRenderers}
                    prefetchMethod="GET"
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '35px',
                        backgroundColor: 'white',
                        zIndex: 10,
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                />
            </Box>
        );
    };

    const renderViewer = () => {
        switch (fileType) {
            case 'pdf':
                return (
                    <Box display="flex" flexDirection="column" alignItems="center"
                        sx={{
                            border: '0px solid #ddd',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            padding: '2px',
                            width: '100%',
                            height: '90vh',
                            position: 'relative'
                        }}
                    >
                        <iframe
                            src={file.fileUrl}
                            onLoad={handleIframeLoad}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            title="PDF Viewer"
                        />
                    </Box>
                );
            case 'image':
                return (
                !loading ?    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: '100%',
                            minHeight: '50vh',
                        }}
                    >
                        <img
                            src={file.fileUrl}
                            alt="Uploaded file"
                            style={{ width: '100%',maxHeight: '80vh', maxWidth: '700px', display: 'block', margin: '0 auto' }}
                        />
                    </Box> : <></>
                );
            case 'word':
            case 'powerpoint':
            case 'excel':
                return renderDocViewer(file);
            case 'video':
                return (
                    <video
                        controls
                        src={file.fileUrl}
                        onLoadedData={handleLoadComplete}
                        style={{
                            width: '90%',
                            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
                            borderRadius: '8px',
                        }}
                    />
                );
            case 'audio':
                return (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        sx={{
                            padding: '5px',
                            width: '30%',
                            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        <audio
                            controls
                            src={file.fileUrl}
                            onLoadedData={handleLoadComplete}
                            style={{
                                width: '100%',
                                borderRadius: '8px',
                            }}
                        />
                    </Box>
                );
            case 'text':
            case 'code':
                return (
                    <iframe
                        src={file.fileUrl}
                        onLoad={handleLoadComplete}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Document Viewer"
                        aria-label="Text or code document preview"
                    />
                );
            default:
                return !loading && <Typography>Unsupported file type. Please upload a supported file (e.g., PDF, Excel, Word, Image).</Typography>;
        }
    };

    return (
        <Box display="flex" justifyContent="center" alignItems='center' sx={{ height: '100%', width: '100%', minHeight: '20vh' }}>

            {loading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1
                    }}
                >
                     <CircularProgress />
                </Box>
            )}
            {errorMessage ? (
                <Box
                    sx={{
                        color: 'red',
                        padding: '10px',
                        margin: '10px auto',
                        width: 'fit-content',
                        textAlign: 'center',
                        zIndex: 2,
                        fontSize: '16px',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <CancelOutlinedIcon fontSize='large' />
                    {errorMessage}
                </Box>
            ) :
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    width={"100%"}
                >
                    {/* <Button
                        variant="contained"
                        component="label"
                        endIcon={<DownloadIcon sx={{ color: theme.palette.primary.contrastText }} />}
                        fullWidth
                        sx={{
                            textTransform: "none",
                            py: 1,
                            borderColor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                            width: "fit-content",
                            alignSelf: "flex-end"
                        }}
                        onClick={downloadFile}
                        disabled={!file?.fileUrl}
                    >
                        Download File
                    </Button> */}
                    {renderViewer()}
                </Box>
            }
        </Box>
    );
};

export default DocumentViewer;
